import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  IconButton,
  Typography,
  ListItem,
  Paper,
  Grid,
  Divider,
  Button,
  TextField,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import UnfoldLessIcon from '@material-ui/icons/UnfoldLess';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import Tooltip from '@material-ui/core/Tooltip';
import { useGetOne, useUpdate, useNotify } from 'react-admin';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { ViewOnlyQuoteAnswerList } from './QuoteQuestionFields';

import InitialsAvatar from '../../zipchat/InitialsAvatar';
import UnifiedVehicle from '../scheduled_work/UnifiedVehicle/UnifiedVehicle';
import QuoteButton from './QuoteButton';
import { QuickQuoteContext } from '../../Context';
import { formatPrice, convertPhoneFormat } from '../../helper';

const useStyles = makeStyles(() => ({
  listItem: {
    width: '100%',
    padding: '0em',
  },
  backButton: {
    position: 'absolute',
    left: '0.3em',
    top: '0.3em',
  },
  listChildren: {
    width: '100%',
    padding: '.75em 0em',
  },
  requestResponses: { padding: '0em 2em' },
  adminNotesContainer: {
    backgroundColor: 'whitesmoke',
    padding: '10px',
  },
}));

const QuoteRequestInfo = ({
  quoteRequestData,
  quoteAnswers = [],
  onClickAway = null,
  includeBuildLink = false,
  onMinimize = null,
  isMinimizedDefault = false,
  isEditable = false,
  quote = {},
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const { vehicleData } = useContext(QuickQuoteContext);
  const {
    coupon_code: couponCode,
    custom_discount_amount_dollars: customDiscountAmount,
    custom_discount_reason: customDiscountReason,
  } = quote;

  const [adminNote, setAdminNote] = useState(quoteRequestData?.admin_note);
  const [savedAdminNote, setSavedAdminNote] = useState(
    quoteRequestData?.admin_note,
  );
  const [adminNoteIsSaving, setAdminNoteIsSaving] = useState(false);
  const [updateAdminNote] = useUpdate(
    'quote-request',
    quoteRequestData.id,
    { admin_note: adminNote },
    quoteRequestData,
    {
      onSuccess: () => {
        setSavedAdminNote(adminNote);
        setAdminNoteIsSaving(false);
      },
      onFailure: (error) =>
        notify(`Error when saving internal note: ${error.message}`, 'warning'),
    },
  );

  const saveAdminNote = () => {
    setAdminNoteIsSaving(true);
    updateAdminNote(adminNote);
  };

  const adminNoteIsSaved = adminNote === savedAdminNote;

  useEffect(() => {
    if (adminNoteIsSaved) return () => {};
    const timeout = setTimeout(saveAdminNote, 1000);
    return () => clearTimeout(timeout);
  }, [adminNote]);

  const [isMinimized, setIsMinimized] = useState(isMinimizedDefault);
  const onClickMinimizeMaximize = (shouldMinimize) => {
    setIsMinimized(shouldMinimize);
    onMinimize(shouldMinimize);
  };

  const { data: customerData } = useGetOne(
    'customer',
    quoteRequestData?.customer_id,
  );

  const adminNoteSavedIndicator = adminNoteIsSaving
    ? 'Saving...'
    : adminNoteIsSaved
    ? 'Saved'
    : 'Editing...';

  return (
    <div>
      <ListItem className={classes.listItem} divider>
        <Paper className={classes.listChildren}>
          <Grid
            container
            spacing={0}
            direction="column"
            justify="center"
            alignItems="center"
          >
            {onClickAway && (
              <IconButton
                size="small"
                aria-label="back"
                className={classes.backButton}
                onClick={onClickAway}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            {onMinimize && (
              <Tooltip
                title={`Show ${isMinimized ? 'more' : 'less'}`}
                placement="bottom"
              >
                <IconButton
                  size="small"
                  aria-label={isMinimized ? 'expand' : 'collapse'}
                  className={classes.backButton}
                  onClick={() => onClickMinimizeMaximize(!isMinimized)}
                >
                  {isMinimized ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
                </IconButton>
              </Tooltip>
            )}
            {customerData ? (
              <Grid item>
                <Grid
                  container
                  spacing={0}
                  direction="column"
                  justify="center"
                  alignItems="center"
                >
                  <Grid item>
                    <a href={`/#/customer/${customerData.id}/show`}>
                      <InitialsAvatar
                        name={`${customerData.first_name} ${customerData.last_name}`}
                        size="50"
                        color="rgba(0, 0, 0, 0.85)"
                        modifierFromTop={0.32}
                      />
                    </a>
                  </Grid>
                  <Grid item>
                    <Typography component="h1">
                      {customerData.first_name} {customerData.last_name}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography component="h1">{customerData.email}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography component="h1">
                      {convertPhoneFormat(customerData.phone)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <Grid item>
                <Grid
                  container
                  spacing={0}
                  direction="column"
                  justify="center"
                  alignItems="center"
                >
                  {quoteRequestData?.customer_email && (
                    <Grid item>
                      <Typography>
                        <strong>Customer Email: </strong>
                        {quoteRequestData.customer_email}
                      </Typography>
                    </Grid>
                  )}
                  {quoteRequestData?.customer_phone && (
                    <Grid item>
                      <Typography>
                        <strong>Customer Phone: </strong>
                        {convertPhoneFormat(quoteRequestData.customer_phone)}
                      </Typography>
                    </Grid>
                  )}
                  {!(
                    quoteRequestData?.customer_email ||
                    quoteRequestData?.customer_phone
                  ) && (
                    <Grid item>
                      <Typography>No customer on file</Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Paper>
      </ListItem>
      {vehicleData && !isMinimized && (
        <ListItem className={classes.listItem} divider>
          <Paper className={classes.listChildren}>
            <UnifiedVehicle vehicle={vehicleData} showTitle={false} />
          </Paper>
        </ListItem>
      )}

      {!isMinimized && (
        <>
          <ListItem className={classes.listItem}>
            <Paper className={classes.listChildren}>
              <Typography
                component="h1"
                style={{
                  fontSize: '1.2em',
                  fontWeight: '600',
                  margin: '0em 0.5em 0.5em',
                }}
              >
                Customer Request
              </Typography>
              <div className={classes.requestResponses}>
                <Grid md={12} className={classes.adminNotesContainer}>
                  <Typography>
                    <strong>Internal notes:</strong>
                  </Typography>
                  {isEditable ? (
                    <Grid
                      item
                      style={{ display: 'flex', alignItems: 'flex-end' }}
                    >
                      <Tooltip
                        title="These notes will not be shown to customers"
                        placement="left"
                      >
                        <TextField
                          placeholder="Add an internal note..."
                          type="text"
                          InputProps={{
                            disableUnderline: true,
                          }}
                          label={null}
                          fullWidth
                          multiline
                          value={adminNote}
                          onChange={(event) => setAdminNote(event.target.value)}
                        />
                      </Tooltip>
                      <Button disabled onClick={saveAdminNote}>
                        {adminNoteSavedIndicator}
                      </Button>
                    </Grid>
                  ) : (
                    <Typography variant="p">{adminNote}</Typography>
                  )}
                </Grid>
                {quoteAnswers?.length ? (
                  <ViewOnlyQuoteAnswerList answers={quoteAnswers} />
                ) : (
                  <Typography variant="p">
                    No additional details provided
                  </Typography>
                )}
              </div>
            </Paper>
          </ListItem>
          <ListItem className={classes.listItem}>
            <Paper className={classes.listChildren}>
              <Typography
                component="h1"
                style={{
                  fontSize: '1.2em',
                  fontWeight: '600',
                  margin: '0em 0.5em 0.5em',
                }}
              >
                Coupons and Discounts
              </Typography>
              <div className={classes.requestResponses}>
                {!!couponCode && (
                  <Typography style={{ paddingBottom: 10 }}>
                    <strong>Coupon:</strong> {couponCode} - Click the
                    <MoreVertIcon />
                    above and create an appointment to see the effect on cost
                  </Typography>
                )}
                {!!customDiscountAmount && (
                  <Typography>
                    <strong>Custom Discount:</strong>{' '}
                    {formatPrice(customDiscountAmount)}
                    {customDiscountReason && ` for ${customDiscountReason}`}
                  </Typography>
                )}
                {!(couponCode || customDiscountAmount) && (
                  <Typography>
                    No coupon or discount attached to quote
                  </Typography>
                )}
              </div>
            </Paper>
          </ListItem>
        </>
      )}
      {includeBuildLink && (
        <div>
          <Divider />
          <ListItem className={classes.listItem}>
            <Divider />

            <Paper className={classes.listChildren}>
              <Grid
                container
                spacing={0}
                direction="column"
                justify="center"
                alignItems="center"
              >
                <Grid item>
                  <Button
                    disabled={adminNote === quoteRequestData?.admin_note}
                    onClick={updateAdminNote}
                  >
                    Save
                  </Button>
                </Grid>
                <Grid item>
                  <QuoteButton record={quoteRequestData} source="id" />
                </Grid>
              </Grid>
            </Paper>
          </ListItem>
        </div>
      )}
    </div>
  );
};

export default QuoteRequestInfo;
